/**
 * 将Tiptap编辑器的JSON格式转换为项目Outline的JSON格式
 * 以及将Outline的JSON格式转换为Tiptap编辑器的JSON格式
 */
import useOutlineStore from "../stores/modules/outline";
import { NODE_TYPE_MAPPING, MARK_TYPE_MAPPING, latexCover, uploadLatexCover, listFormatFn } from "./const";
// 生成唯一ID
let nextId = 1;
const generateId = () => nextId++;
// 引用ID计数器
// let referNextId = 1;
let figureNextId = 1;
const genFigureNextId = () => figureNextId++;
let equationNextId = 1;
const genEquationNextId = () => equationNextId++;
let tableNextId = 1;
const genTableNextId = () => tableNextId++;
let footnoteNextId = 1;
let labelMap = {}
/*
 * 将Tiptap编辑器的JSON格式转换为Outline的JSON格式
 * @param tiptapJson Tiptap编辑器的JSON数据，直接将Tiptap的整个对象传入  * @returns 转换后的Outline JSON数据
 */
const convertTiptapToOutlineJson = (tiptapJson: any, currentSectionIndex?: number) => {
  const outline = useOutlineStore.getState();
  // 重置ID计数器
  nextId = outline.nextId;
  // referNextId = 1;
  figureNextId = outline.figureNextId;
  equationNextId = outline.equationNextId;
  tableNextId = outline.tableNextId;
  footnoteNextId = 1;
  labelMap = outline.labelMap

  const tiptapJsonContent = tiptapJson.content;
  // 如果第一个节点是heading，则将其作为根节点，目前判断level,后续看情况是否要加上，如果这里处理了，注意rephrase那边
  if (
    tiptapJsonContent &&
    tiptapJsonContent.length > 0 &&
    tiptapJsonContent[0].type === "heading"
  ) {
    const firstNode = tiptapJsonContent[0];
    const rootNode: any = {
      // 优先使用节点中的ID，如果没有则生成新ID
      id: outline?.outline?.nodes?.[currentSectionIndex]?.id || firstNode.attrs?.id || generateId(),
      name: extractTextContent(firstNode),
      type: "section",
      label: extractTextContent(firstNode),
      level: firstNode.attrs?.level || 1,
      content: "",
      children: [],
    };
    // 处理heading节点中的reference
    if (firstNode.content && Array.isArray(firstNode.content)) {
      processInlineContent(firstNode.content, rootNode, true);
    }
    rootNode.label = firstNode.attrs?.label || `${rootNode.label}_${rootNode.id}`;
    // 处理剩余节点
    if (tiptapJsonContent.length > 1) {
      processChildren(tiptapJsonContent.slice(1), rootNode);
    }

    outline.setNextId(nextId);
    outline.setFigureNextId(figureNextId)
    outline.setEquationId(equationNextId)
    outline.setTableNextId(tableNextId)
    outline.setLabelMap(labelMap)

    return rootNode;
  }
  // 如果没有heading节点，则创建默认根节点
  const rootNode = {
    id: generateId(),
    name: "Untitled",
    type: "section",
    label: "untitled_1",
    level: 1,
    content: "",
    children: [],
  };

  // 处理Tiptap文档
  if (tiptapJsonContent) {
    processChildren(tiptapJsonContent, rootNode);
  }
  return rootNode;
};

/**
 * 处理Tiptap节点的子节点
 * @param children Tiptap子节点数组
 * @param parentNode 父节点
 */
const processChildren = (children: any[], parentNode: any) => {
  if (!children || !Array.isArray(children)) return;

  let i = 0;
  while (i < children.length) {
    const child = children[i];
    // const nodeLevel = BLOCK_ELEMENT.includes(child.type) ? 2 : (child.attrs?.level || parentNode.level);
    const nodeLevel = child.attrs?.level || parentNode.level;

    if (child.type === "heading") {
      if (nodeLevel <= parentNode.level) {
        // 当前 heading 层级小于等于父节点，说明属于上层，返回上一层处理
        return i;
      }
      // 创建新的 section 节点
      const currentSectionNode: any = {
        // 优先使用节点中的ID，如果没有则生成新ID
        id: child.attrs?.id || generateId(),
        name: extractTextContent(child),
        type: "section",
        label: `${extractTextContent(child)}`,
        level: nodeLevel,
        content: "",
        parentId: parentNode.id,
        children: [],
      };

      // 处理 reference
      if (child.content && Array.isArray(child.content)) {
        processInlineContent(child.content, currentSectionNode, true);
      }
      parentNode.children.push(currentSectionNode);
      // 递归处理属于当前 heading 的子节点
      i++;
      while (i < children.length) {
        const nextChild = children[i];
        const nextLevel = nextChild.attrs?.level || parentNode.level;
        if (nextLevel <= nodeLevel) {
          // 下一个节点层级小于等于当前 heading，交给上一层处理
          break;
        }
        // 递归处理，i 会被子递归消耗
        const consumed = processChildren(children.slice(i), currentSectionNode);
        if (typeof consumed === "number" && consumed > 0) {
          i += consumed;
        } else {
          i++;
        }
      }
    } else {
      // 如果是段落节点且有明确的level属性，需要找到合适的父节点
      if (
        child.type === "paragraph" &&
        child.attrs &&
        typeof child.attrs.level === "number"
      ) {
        // 如果段落的level小于等于当前父节点的level，需要向上查找合适的父节点
        if (child.attrs.level <= parentNode.level) {
          // 返回当前位置，让上层处理
          return i;
        }
      }
      // 直接使用当前父节点作为目标节点，保持原始结构
      const targetNode = parentNode;
      const node = processNode(child, targetNode, targetNode.level);
      if (node) {
        targetNode.children.push(node);
        if (
          child.content &&
          (child.type === "listItem" ||
            child.type === "bulletList" ||
            child.type === "orderedList") &&
          !["table", "tableRow", "tableCell", "tableHeader"].includes(
            child.type
          )
        ) {
          processChildren(child.content, node);
        }
      }
      i++;
    }
  }
  return i;
};

/**
 * 处理单个Tiptap节点, 其实可以跟processChildren合并，但是为了区分功能，分开了
 * @param node Tiptap节点
 * @param parentNode 父节点
 * @param level 当前层级
 * @returns 处理后的节点
 */
const processNode = (node: any, parentNode: any, level: number) => {
  if (!node || !node.type) return null;

  if (node.type === "codeBlock") {
    const content = node.content && node.content[0] && node.content[0].text
    ? node.content[0].text
    : ""
    let label = content.match(/\\label\{([^}]*)\}/)?.[1]

    const oldLabel = label
    if (label && label !== 'Enter label here') {
      if (labelMap[label]) {
        label = `${label}_${labelMap[label]}`
        labelMap[label] =  labelMap[label] + 1
      } else {
        labelMap[label] = 1
      }
    }
    

    const codeObj = {
      id: node.attrs?.id || generateId(),
      level: 2,
      parentId: parentNode.id,
      language: node.attrs?.language || "",
      content: "",
    }
 
    if (node.attrs?.useType === 'Table') {
      return {
        ...codeObj,
        type: "latex_table",
        latex: oldLabel ? content.replace(new RegExp(`\label{${oldLabel}}`), `\label{${label}}`) : content,
        caption: node?.attrs?.title,
        label:  label || node?.attrs?.label  || `table_${genTableNextId()}`
      }
    } if (node.attrs?.useType === 'LaTeX') {
      return {
        ...codeObj,
        type: "latex",
        label: label || node?.attrs?.label || `equation_${genEquationNextId()}`,
        latex: oldLabel ? content.replace(new RegExp(`\label{${oldLabel}}`), `\label{${label}}`) : content,
      }
    }else {
      return {
        ...codeObj,
        type: "code",
        content
      };
    }
  }

  if (node.type === "imageCode" && node?.content?.[0]?.type === 'codeBlock') {
    const useType = node?.content?.[0]?.attrs?.useType || "";
    const fileName = node?.content?.[0]?.attrs?.fileName || "";
    const content = node?.content[0]?.content?.[0]?.text
    let label = content.match(/\\label\{([^}]*)\}/)?.[1]
    
    const oldLabel = label
    if (label && label !== 'Enter label here') {
      if (labelMap[label]) {
        label = `${label}_${labelMap[label]}`
        labelMap[label] =  labelMap[label] + 1
      } else {
        labelMap[label] = 1
      }
    }
    const codeObj = {
      id: node.attrs?.id || generateId(),
      level: 2,
      parentId: parentNode.id,
      language: node.attrs?.language || "",
      img_url: node.attrs?.src,
      file_name: fileName,
      content: "",
    }
    if (useType === 'TableFigure') {
      return {
       ...codeObj,
       caption: node?.attrs?.title,
        type: "figure_table",
        label: `table_${genTableNextId()}`,
      }
    } else if (useType === 'Table') {
      return {
        ...codeObj,
         type: "latex_figure_table",
         label: label || node?.attrs?.label  || `table_${genTableNextId()}`,
         latex:  oldLabel ? content.replace(new RegExp(`\label{${oldLabel}}`), `\label{${label}}`) : content,
       }
    } else if (useType === 'FigureLatex') {
      return {
        ...codeObj,
         type: "latex_figure",
         label: label || node.attrs?.label  || `figure_${genFigureNextId()}`,
         latex: oldLabel ? content.replace(new RegExp(`\label{${oldLabel}}`), `\label{${label}}`) : content,
       }
    } else {
      return {
       ...codeObj,
         type: "figure",
         caption: node?.attrs?.title,
         label: node.attrs?.label || `figure_${genFigureNextId()}`,
       }
    }
  }

  if (node.type === "orderedList") {
    return listFormatFn('ol', node, parentNode, processInlineContent, generateId, processNode);
  }

  if (node.type === "bulletList") {
    return listFormatFn('ul', node, parentNode, processInlineContent, generateId, processNode);
  }
    
  // 移除单独的listItem处理，因为已经在orderedList中处理了
  if (node.type === "listItem") {
    return null;
  }

  // comment 容器：参考列表处理为容器节点，内部内容作为 children
  if (node.type === "comment") {
    const commentNode: any = {
      id: node.attrs?.id || generateId(),
      type: "comment",
      level: node.attrs?.level || level + 1,
      parentId: parentNode.id,
      name: node.attrs?.title || "",
      label: node.attrs?.label || "",
      content: "",
      children: [],
    };

    // 将内部 content 的各类块级/内联节点转为 children
    if (Array.isArray(node.content)) {
      node.content.forEach((contentItem: any) => {
        const child = processNode(contentItem, commentNode, commentNode.level);
        if (child) {
          commentNode.children.push(child);
        }
      });
    }
    return commentNode;
  }

  // 映射节点类型
  const nodeType = NODE_TYPE_MAPPING[node.type] || "paragraph";

  // 创建新节点
  const newNode: any = {
    id: node.attrs?.id || generateId(),
    type: nodeType,
    level: node.attrs?.level || level + 1,
    parentId: parentNode.id,
    children: [],
  };

  // 根据节点类型设置属性
  if (nodeType === "section") {
    // section类型只设置name和label，不设置content，也不递归处理heading的content
    if (node.type === "heading") {
      newNode.name = extractTextContent(node);
      newNode.label = extractTextContent(node)
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\s/g, "_");

      // 处理heading节点中的reference
      if (node.content && Array.isArray(node.content)) {
        processInlineContent(node.content, newNode, true);
      }

      newNode.label = `${newNode.label}`;
      return newNode;
    } else {
      newNode.name = "Untitled Section";
      newNode.label = `untitled_${newNode.id}`;
    }
  } else {
    // 非section类型只设置content，不设置name
    if (nodeType === "figure" || nodeType === "image") {
      if (node.attrs?.src) {
        newNode.content = node.attrs.src;
        newNode.label = node.attrs?.label || `figure_${genFigureNextId()}`;
        newNode.caption = [node.attrs.title || `Fig. ${genFigureNextId()}. Image`];
      }
    } else if (nodeType === "table") {
      if (node.type === "table") {
        newNode.content = processTableContent(node);
        newNode.label = node.attrs?.label || `table_${genTableNextId()}`;
        newNode.caption = node.attrs.caption;
      }
    } else if (nodeType === "equation") {
      if (
        (node.type === "equation" || node.type === "blockEquation") &&
        node.attrs?.latex
      ) {
        newNode.content = node.attrs.latex;
        newNode.label = node?.attrs?.label || `equation_${equationNextId}`;
        newNode.equation = {
          [equationNextId]: {
            value: node.attrs.latex,
          },
        };
        equationNextId++;
      }
    } else if (nodeType === "footnote") {
      newNode.footnote = {
        [footnoteNextId]: {
          value: node.attrs.content,
        },
      };
      footnoteNextId++;
    } else {
      // 处理段落和其他内容节点
      if (
        node.type === "paragraph" ||
        node.type === "listItem" ||
        node.type === "bulletList" ||
        node.type === "orderedList"
      ) {
        newNode.content = processTextContent(node);
      } else if (node.type === "codeBlock") {
        // codeBlock没有子级，先不删
        newNode.content = `<pre><code>${
          node.content ? extractTextContent(node) : ""
        }</code></pre>`;
      } else {
        newNode.content = extractTextContent(node);
      }

      // 处理内联公式和引用
      if (node.content) {
        processInlineContent(node.content, newNode);
      }
    }
  }
  return newNode;
};
/**
 * 处理内联引用，heading放到name，否则放到content
 */
const processInlineContent = (content, newNode, isHead = false) => {
  let equationIndex = 1;
  let referencesIndex = 1;
  let citesIndex = 1;
  let footnoteIndex = 1;
  const inlineContent = isHead ? "name" : "content";
  
  // 初始化内容为空字符串，inline的时候需要保证原来的顺序，转换的时候置空
  newNode[inlineContent] = "";
  // 按顺序处理所有节点
  content.forEach((child: any) => {
    if (child.type === "text") {
      newNode[inlineContent] += processTextContent(child);
    } else if (child.type === "equation" || child.type === "inlineEquation") {
      if (!newNode.equation) newNode.equation = {};
      const equationContent = `($equation{${equationIndex}}$)`;
      newNode[inlineContent] += equationContent;
      newNode.equation[equationIndex] = {
        value: child.attrs.latex,
      };
      equationIndex++;
    } else if (child.type === "reference") {
      if (child.attrs.type === "cite") {
        if (!newNode.cite) newNode.cite = {};
        const citeContent = `($cite{${citesIndex}}$)`;
        newNode[inlineContent] += citeContent;
        newNode.cite[citesIndex] = {
          value: child.attrs.tooltip || "",
          id: child.attrs.content || "",
          type: "cite",
        };
        citesIndex++;
      } else {
        if (!newNode.refer) newNode.refer = {};
        const refContent = `($refer{${referencesIndex}}$)`;
        newNode[inlineContent] += refContent;
        newNode.refer[referencesIndex] = {
          content: child.attrs.tooltip || child.attrs.content,
          value: child.attrs.content || "",
          type: child.attrs?.type === "text" ? "section" : child.attrs?.type,
        };
        referencesIndex++;
      }
    } else if (child.type === "footnote" && child.attrs?.content) {
      if (!newNode.footnote) newNode.footnote = {};
      const footnoteContent = `($footnote{${footnoteIndex}}$)`;
      newNode[inlineContent] += footnoteContent;
      newNode.footnote[footnoteIndex] = {
        value: child.attrs.content,
      };
      footnoteIndex++;
    }
  });
};

/**
 * 提取节点中的文本内容
 * @param node Tiptap节点
 * @returns 文本内容
 */
const extractTextContent = (node: any): string => {
  if (!node) return "";

  if (node.type === "text") {
    return node.text || "";
  }

  if (node.content && Array.isArray(node.content)) {
    return node.content.map(extractTextContent).join("");
  }

  return "";
};

/**
 * 处理文本内容，包括标记和特殊节点
 * @param node Tiptap节点
 * @returns 处理后的文本内容
 */
const processTextContent = (node: any): string => {
  if (!node) return "";

  if (node.type === "text") {
    // 处理纯文本节点
    let text = node.text || "";

    // 处理标记
    if (node.marks && node.marks.length > 0) {
      node.marks.forEach((mark: any) => {
        const markType = MARK_TYPE_MAPPING[mark.type] || mark.type;
        if (markType === "a" && mark.attrs?.href) {
          text = `<a href="${mark.attrs.href}">${text}</a>`;
        } else {
          text = `<${markType}>${text}</${markType}>`;
        }
      });
    }
    return text;
  } else if (node.content && Array.isArray(node.content)) {
    // 递归处理有子内容的节点
    let content = "";

    // 处理列表项
    if (node.type === "bulletList") {
      content = "<ul>";
      content += node.content
        .map((item: any) => `<li>${processTextContent(item)}</li>`)
        .join("");
      content += "</ul>";
    } else if (node.type === "orderedList") {
      content = "<ol>";
      content += node.content
        .map((item: any) => `<li>${processTextContent(item)}</li>`)
        .join("");
      content += "</ol>";
    } else if (node.type === "listItem") {
      content = node.content.map(processTextContent).join("");
    } else {
      content = node.content.map(processTextContent).join("");
    }

    return content;
  }
  // 处理特殊节点
  if (node.type === "hardBreak") {
    return "<br>";
  } else if (node.type === "horizontalRule") {
    return "<hr>";
  } else if (node.type === "blockEquation") {
    return `($equation{${node.attrs.number || 1}}$)`;
  }
  return "";
};

/**
 * 处理表格内容
 * @param tableNode Tiptap表格节点
 * @returns 处理后的HTML表格内容
 */
const processTableContent = (tableNode: any): string => {
  if (!tableNode.content || !Array.isArray(tableNode.content)) {
    return "<table><tr><td>空表格</td></tr></table>";
  }

  let tableHtml = "<html><body><table>";

  // 处理表格行
  tableNode.content.forEach((rowNode: any) => {
    if (rowNode.type === "tableRow" && rowNode.content) {
      tableHtml += "<tr>";

      // 处理单元格
      rowNode.content.forEach((cellNode: any) => {
        const isHeader = cellNode.type === "tableHeader";
        const tag = isHeader ? "th" : "td";
        const colspan = cellNode.attrs?.colspan || 1;
        const rowspan = cellNode.attrs?.rowspan || 1;

        tableHtml += `<${tag}`;

        if (colspan > 1) {
          tableHtml += ` colspan="${colspan}"`;
        }

        if (rowspan > 1) {
          tableHtml += ` rowspan="${rowspan}"`;
        }

        tableHtml += ">";

        // 处理单元格内容
        if (cellNode.content) {
          cellNode.content.forEach((contentNode: any) => {
            tableHtml += processTextContent(contentNode);
          });
        }

        tableHtml += `</${tag}>`;
      });

      tableHtml += "</tr>";
    }
  });

  tableHtml += "</table></body></html>";
  return tableHtml;
};

/**
 * 将Outline的JSON格式转换回Tiptap编辑器的JSON格式
 * @param outlineJson Outline的JSON数据 ，传入当前index的数据
 * @returns 转换后的Tiptap编辑器JSON数据
 */
const convertOutlineToTiptapJson = (outlineJson: any) => {

  if (!outlineJson) {
    return { type: "doc", content: [] };
  }

  const tiptapDoc = {
    type: "doc",
    content: [],
  };

  // 递归处理根节点
  outlineNodeToTiptapContent(outlineJson, tiptapDoc.content);
  
  return tiptapDoc;
};

/**
 * 递归将outline节点转换为tiptap content
 */
function outlineNodeToTiptapContent(node: any, parentContent: any[]) {
  if (!node) return;

  if (node.type === "code") {
    // 避免空文本节点：当内容为空时，不生成 text 子节点
    const contentArray = node.content && node.content.length > 0
      ? [
          {
            type: "text",
            text: node.content,
          },
        ]
      : [];
    parentContent.push({
      type: "codeBlock",
      attrs: {
        language: node.language || "text",
        id: node.id,
      },
      content: contentArray,
    });
    return;
  } 
  if (node.type === "latex_table") {
    parentContent.push(latexCover(node, "Table"));
    return;
  }
  if (node.type === "latex") {
    parentContent.push(latexCover(node, "LaTeX"));
    return;
  }
  if (node.type === "figure_table") {
    parentContent.push(uploadLatexCover(node, "TableFigure"));
    return;
  }
  if (node.type === "latex_figure_table") {
    parentContent.push(uploadLatexCover(node, "Table"));
    return;
  }
  if (node.type === "latex_figure") {
    parentContent.push(uploadLatexCover(node, "FigureLatex"));
    return;
  }

  // comment 容器：参考列表，内部 children 逐个转换；content 作为首段落
  if (node.type === "comment") {
    const commentContent: any[] = [];

    // 如果有纯文本 content，作为一个段落插入到 comment 内容首位
    if (node.content) {
      const paragraphContent: any[] = outlineTextToTiptapContent(
        node.content,
        node.refer,
        node.equation,
        node.footnote,
        node.cite
      );
      commentContent.push({
        type: "paragraph",
        attrs: {
          textAlign: null,
          level: node.level || 2,
          id: null,
        },
        content: paragraphContent,
      });
    }

    // 转换 children
    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach((childNode: any) => {
        const childArr: any[] = [];
        outlineNodeToTiptapContent(childNode, childArr);
        if (childArr.length > 0) {
          commentContent.push(...childArr);
        }
      });
    }

    parentContent.push({
      type: "comment",
      attrs: {
        collapsed: false,
        title: node.name || "Comment",
        level: node.level || 2,
        id: node.id,
        label: node.label || "",
      },
      content: commentContent,
    });
    return;
  }

  if (node.type === "ol") {
    parentContent.push({
      type: "orderedList",
      attrs: {
        tight: true,
        start: 1,
        type: null,
        id: node.id,
      },
      content: (node.children || []).map((liNode: any) => {
        const listItemContent = [];

        // 添加列表项的主要内容（段落）
        if (liNode.content !== undefined) {
          listItemContent.push({
            type: "paragraph",
            attrs: {
              textAlign: null,
              level: liNode.level || 2,
              id: liNode.id,
            },
            content: outlineTextToTiptapContent(
              liNode.content || "",
              liNode.refer,
              liNode.equation,
              liNode.footnote,
              liNode.cite
            ),
          });
        }

        // 处理子元素，区分嵌套列表和其他内容
        if (liNode.children && liNode.children.length > 0) {
          liNode.children.forEach((childNode) => {
            if (childNode.type === "ol" || childNode.type === "ul") {
              // 嵌套列表：递归调用转换函数
              outlineNodeToTiptapContent(childNode, listItemContent);
            } else {
              // 其他内容：正常处理
              const childContent = [];
              outlineNodeToTiptapContent(childNode, childContent);
              if (childContent.length > 0) {
                listItemContent.push(...childContent);
              }
            }
          });
        }

        return {
          type: "listItem",
          content: listItemContent,
          attrs: {
            textAlign: null,
            level: liNode.level || 3,
            id: liNode.id,
          },
        };
      }),
    });
    return;
  }

  // ul 转 bulletList
  if (node.type === "ul") {
    parentContent.push({
      type: "bulletList",
      attrs: {
        tight: true,
        start: 1,
        type: null,
        id: node.id,
      },
      content: (node.children || []).map((liNode: any) => {
        const listItemContent = [];

        // 添加列表项的主要内容（段落）
        if (liNode.content !== undefined) {
          listItemContent.push({
            type: "paragraph",
            attrs: {
              textAlign: null,
              level: liNode.level || 2,
              id: liNode.id,
            },
            content: outlineTextToTiptapContent(
              liNode.content || "",
              liNode.refer,
              liNode.equation,
              liNode.footnote,
              liNode.cite
            ),
          });
        }

        // 处理子元素，区分嵌套列表和其他内容
        if (liNode.children && liNode.children.length > 0) {
          liNode.children.forEach((childNode) => {
            if (childNode.type === "ol" || childNode.type === "ul") {
              // 嵌套列表：递归调用转换函数
              outlineNodeToTiptapContent(childNode, listItemContent);
            } else {
              // 其他内容：正常处理
              const childContent = [];
              outlineNodeToTiptapContent(childNode, childContent);
              if (childContent.length > 0) {
                listItemContent.push(...childContent);
              }
            }
          });
        }

        return {
          type: "listItem",
          content: listItemContent,
          attrs: {
            textAlign: null,
            level: liNode.level || 3,
            id: liNode.id,
          },
        };
      }),
    });
    return;
  }

  // li 转 listItem（一般只在 ol/ul 下递归用到）
  if (node.type === "li") {
    const listItemContent = [];

    // 添加段落内容
    if (node.content !== undefined) {
      listItemContent.push({
        type: "paragraph",
        attrs: {
          textAlign: null,
          level: node.level || 2,
          id: node.id,
        },
        content: outlineTextToTiptapContent(
          node.content || "",
          node.refer,
          node.equation,
          node.footnote,
          node.cite
        ),
      });
    }
    
    // 处理子元素（可能包含嵌套列表）
    if (node.children && node.children.length > 0) {
      node.children.forEach((childNode) => {
        outlineNodeToTiptapContent(childNode, listItemContent);
      });
    }
    
    return {
      type: "listItem",
      content: listItemContent,
      attrs: {
        textAlign: null,
        level: node.level || 3,
        id: node.id,
      },
    };
  }

  if (node.type === "section") {
    // 处理标题
    const headingContent: any[] = outlineTextToTiptapContent(
      node.name,
      node.refer,
      node.equation,
      node.footnote,
      node.cite
    );
    parentContent.push({
      type: "heading",
      attrs: {
        textAlign: null,
        level: node.level || 1,
        color: node.level === 1 ? "#1D8AFF" : null,
        id: node.id, // 保留节点ID
        label: node.label
      },
      content: headingContent,
    });
    // 递归处理子节点，但不要将标题内容作为子节点
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child: any) => {
        // 跳过与标题内容相同的段落节点
        if (child.type === "paragraph" && child.content === node.name) {
          return;
        }
        outlineNodeToTiptapContent(child, parentContent);
      });
    }
  } else if (node.type === "paragraph") {
    // 处理段落内容
    const paragraphContent: any[] = outlineTextToTiptapContent(
      node.content || "",
      node.refer,
      node.equation,
      node.footnote,
      node.cite
    );
    parentContent.push({
      type: "paragraph",
      attrs: {
        textAlign: null,
        level: node.level || 2,
        id: node.id, // 保留节点ID
      },
      content: paragraphContent,
    });
  } else if (node.type === "equation") {
    // 处理块级公式
    parentContent.push({
      type: "blockEquation",
      attrs: {
        latex: node.content,
        label: node.label || "",
        id: node.id,
      },
    });
  } else if (node.type === "footnote") {
    // 处理块级公式
    parentContent.push({
      type: "footnote",
      attrs: {
        content: node.content,
        id: node.id,
      },
    });
  } else if (node.type === "figure") {
    // 处理图片
    parentContent.push(uploadLatexCover(node, "Figure"));
    // parentContent.push({
    //   type: "image",
    //   attrs: {
    //     src: node.content,
    //     title: node.caption
    //       ? Array.isArray(node.caption)
    //         ? node.caption[0]
    //         : node.caption
    //       : "",
    //     id: node.id,
    //   },
    // });
  } else if (node.type === "table") {
    // 处理表格
    const tableNode = outlineTableHtmlToTiptapTable(
      node.content,
      node.caption
        ? Array.isArray(node.caption)
          ? node.caption[0]
          : node.caption
        : "",
      node.label
    );
    if (tableNode) parentContent.push(tableNode);
  }
}

/**
 * 将outline的富文本字符串和引用、公式信息转换为tiptap content数组
 */
function outlineTextToTiptapContent(
  text: string,
  refer?: any,
  equation?: any,
  footnote?: any,
  cite?: any
): any[] {
  if (!text) return [];
  // 先处理特殊标记
  const parts = text.split(
    /(\(\$refer\{\d+\}\$\)|\(\$equation\{\d+\}\$\)|\(\$footnote\{\d+\}\$\)|\(\$cite\{\d+\}\$\))/g
  );
  const content: any[] = [];

  parts.forEach((part) => {
    if (!part) return;
    if (/^\(\$refer\{(\d+)\}\$\)$/.test(part)) {
      // 引用
      const refId = part.match(/\d+/)?.[0];
      if (refer && refer[refId]) {
        const ref = refer[refId];
        content.push({
          type: "reference",
          attrs: {
            tooltip: ref.content,
            content: ref.value,
            number: parseInt(refId),
            type: ref.type || "section",
          },
        });
      }
    } else if (/^\(\$equation\{(\d+)\}\$\)$/.test(part)) {
      const eqId = part.match(/\d+/)?.[0];
      if (equation && equation[eqId]) {
        content.push({
          type: "inlineEquation",
          attrs: {
            latex: equation[eqId].value,
            number: parseInt(eqId),
          },
        });
      }
    } else if (/^\(\$footnote\{(\d+)\}\$\)$/.test(part)) {
      const eqId = part.match(/\d+/)?.[0];
      if (footnote && footnote[eqId]) {
        content.push({
          type: "footnote",
          attrs: {
            content: footnote[eqId].value,
          },
        });
      }
    } else if (/^\(\$cite\{(\d+)\}\$\)$/.test(part)) {
      const citeId = part.match(/\d+/)?.[0];
      if (cite && cite[citeId]) {
        const citeNote = cite[citeId];
        content.push({
          type: "reference",
          attrs: {
            tooltip: citeNote.value,
            content: citeNote.id,
            number: parseInt(citeId),
            type: "cite",
          },
        });
      }
    } else {
      // 普通文本和富文本标签
      content.push(...outlineHtmlToTiptapTextNodes(part));
    }
  });

  return content;
}

/**
 * 将outline的富文本HTML片段转换为tiptap的text节点及marks
 */
function outlineHtmlToTiptapTextNodes(html: string): any[] {
  // 简单处理常见的富文本标签
  // 支持 <strong> <em> <u> <s> <a> <code> <ol> <li> 等
  // 递归解析
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  const nodes: any[] = [];

  function parseNode(domNode: any, marks: any[] = []) {
    if (domNode.nodeType === 3) {
      // 文本节点
      if (domNode.nodeValue) {
        nodes.push({
          type: "text",
          text: domNode.nodeValue,
          ...(marks.length > 0
            ? { marks: marks.map((m) => ({ type: m.type, attrs: m.attrs })) }
            : {}),
        });
      }
    } else if (domNode.nodeType === 1) {
      // 元素节点
      let markType = null;
      let markAttrs = undefined;
      switch (domNode.nodeName.toLowerCase()) {
        case "strong":
        case "b":
          markType = "bold";
          break;
        case "em":
        case "i":
          markType = "italic";
          break;
        case "u":
          markType = "underline";
          break;
        case "s":
        case "strike":
          markType = "strike";
          break;
        case "a":
          markType = "link";
          markAttrs = { href: domNode.getAttribute("href") };
          break;
        case "code":
          markType = "code";
          break;
        case "ol":
          nodes.push({
            type: "orderedList",
            attrs: { tight: true, start: 1, type: null },
            content: Array.from(domNode.children).map((li: any) => ({
              type: "listItem",
              content: [
                {
                  type: "paragraph",
                  content: outlineHtmlToTiptapTextNodes(li.innerHTML),
                },
              ],
            })),
          });
          return;
        case "ul":
          nodes.push({
            type: "bulletList",
            attrs: { tight: true, start: 1, type: null },
            content: Array.from(domNode.children).map((li: any) => ({
              type: "listItem",
              content: [
                {
                  type: "paragraph",
                  content: outlineHtmlToTiptapTextNodes(li.innerHTML),
                },
              ],
            })),
          });
          return;
        case "li":
          nodes.push({
            type: "listItem",
            content: [
              {
                type: "paragraph",
                content: outlineHtmlToTiptapTextNodes(domNode.innerHTML),
              },
            ],
          });
          return;
      }
      const newMarks = markType
        ? [...marks, { type: markType, attrs: markAttrs }]
        : marks;
      for (let i = 0; i < domNode.childNodes.length; i++) {
        parseNode(domNode.childNodes[i], newMarks);
      }
    }
  }

  for (let i = 0; i < wrapper.childNodes.length; i++) {
    parseNode(wrapper.childNodes[i]);
  }
  return nodes;
}

/**
 * 将outline的表格HTML转换为tiptap的table节点
 */
function outlineTableHtmlToTiptapTable(html: string, caption?: string, label?: string): any {
  if (!html) return null;
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  const table = wrapper.querySelector("table");
  if (!table) return null;

  const tableNode: any = {
    type: "table",
    attrs: {
      caption: caption || "",
      label: label || ""
    },
    content: [],
  };

  const rows = table.querySelectorAll("tr");
  rows.forEach((tr) => {
    const rowNode: any = {
      type: "tableRow",
      content: [],
    };
    const cells = tr.children;
    for (let i = 0; i < cells.length; i++) {
      const cell: any = cells[i];
      const isHeader = cell.tagName.toLowerCase() === "th";
      const cellNode: any = {
        type: isHeader ? "tableHeader" : "tableCell",
        attrs: {
          colspan: parseInt(cell.getAttribute("colspan") || "1"),
          rowspan: parseInt(cell.getAttribute("rowspan") || "1"),
          colwidth: null,
        },
        content: [
          {
            type: "paragraph",
            attrs: {
              textAlign: cell.style.textAlign || null,
              level: 2,
            },
            content: outlineHtmlToTiptapTextNodes(cell.innerHTML),
          },
        ],
      };
      rowNode.content.push(cellNode);
    }
    tableNode.content.push(rowNode);
  });
  return tableNode;
}
// 导出转换函数，文件注释跟ts等后续补上，目前代码都放一个文件，方便阅读和维护
export { convertTiptapToOutlineJson, convertOutlineToTiptapJson };
